import React, { useState, useEffect } from 'react';
import {
    DocumentArrowUpIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    ArrowPathIcon,
    CloudArrowUpIcon,
    CogIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';

const FileUploadProgress = ({
    progress = 0,
    stage = 'uploading', // 'uploading', 'parsing', 'validating', 'processing', 'writing', 'completed', 'error'
    fileName = '',
    fileSize = 0,
    uploadSpeed = 0,
    processedRecords = 0,
    totalRecords = 0,
    validRecords = 0,
    invalidRecords = 0,
    duplicateRecords = 0,
    contactsUpdated = 0,
    // New props for database write progress
    recordsWritten = 0,
    recordsPending = 0,
    writeProgress = 0,
    error = null,
    onCancel = null
}) => {
    const [elapsedTime, setElapsedTime] = useState(0);
    const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState(0);

    useEffect(() => {
        const timer = setInterval(() => {
            setElapsedTime(prev => prev + 1);
        }, 1000);

        // Calculate estimated time remaining
        if (progress > 0 && progress < 100) {
            const timePerPercent = elapsedTime / progress;
            const remainingPercent = 100 - progress;
            setEstimatedTimeRemaining(Math.round(timePerPercent * remainingPercent));
        }

        return () => clearInterval(timer);
    }, [progress, elapsedTime]);

    const formatTime = (seconds) => {
        if (seconds < 60) return `${seconds}s`;
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}m ${remainingSeconds}s`;
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
    };

    const formatSpeed = (bytesPerSecond) => {
        if (bytesPerSecond === 0) return '0 B/s';
        return `${formatFileSize(bytesPerSecond)}/s`;
    };

    const getStageInfo = () => {
        switch (stage) {
            case 'uploading':
                return {
                    icon: CloudArrowUpIcon,
                    title: 'Uploading File',
                    description: 'Transferring file to server...',
                    color: 'blue'
                };
            case 'parsing':
                return {
                    icon: DocumentArrowUpIcon,
                    title: 'Parsing CSV',
                    description: 'Reading and parsing file structure...',
                    color: 'indigo'
                };
            case 'validating':
                return {
                    icon: CheckCircleIcon,
                    title: 'Validating Data',
                    description: 'Checking phone numbers and data quality...',
                    color: 'purple'
                };
            case 'processing':
                return {
                    icon: CogIcon,
                    title: 'Processing Campaign',
                    description: 'Creating campaign and contacts...',
                    color: 'green'
                };
            case 'writing':
                return {
                    icon: CloudArrowUpIcon,
                    title: 'Writing to Database',
                    description: 'Writing data to the database...',
                    color: 'purple'
                };
            case 'completed':
                return {
                    icon: CheckCircleIcon,
                    title: 'Upload Complete',
                    description: 'File processed successfully!',
                    color: 'green'
                };
            case 'error':
                return {
                    icon: ExclamationTriangleIcon,
                    title: 'Upload Failed',
                    description: error || 'An error occurred during upload',
                    color: 'red'
                };
            default:
                return {
                    icon: ArrowPathIcon,
                    title: 'Processing',
                    description: 'Please wait...',
                    color: 'gray'
                };
        }
    };

    const stageInfo = getStageInfo();
    const IconComponent = stageInfo.icon;

    // Circular progress calculation
    const radius = 45;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - (progress / 100) * circumference;

    return (
        <div className="upload-modal fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="modal-content bg-white rounded-xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden animate-fade-in">
                {/* Modal Content with proper padding */}
                <div className="p-6">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-semibold text-gray-900">File Upload Progress</h3>
                        {onCancel && stage !== 'completed' && stage !== 'error' && (
                            <button
                                onClick={onCancel}
                                className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
                            >
                                <XMarkIcon className="w-5 h-5" />
                            </button>
                        )}
                    </div>

                    {/* Circular Progress */}
                    <div className="flex flex-col items-center mb-6">
                        <div className="relative w-32 h-32 mb-4">
                            {/* Background circle */}
                            <svg className="w-32 h-32 transform -rotate-90" viewBox="0 0 100 100">
                                <circle
                                    cx="50"
                                    cy="50"
                                    r={radius}
                                    stroke="currentColor"
                                    strokeWidth="8"
                                    fill="transparent"
                                    className="text-gray-200"
                                />
                                {/* Progress circle */}
                                <circle
                                    cx="50"
                                    cy="50"
                                    r={radius}
                                    stroke="currentColor"
                                    strokeWidth="8"
                                    fill="transparent"
                                    strokeDasharray={circumference}
                                    strokeDashoffset={strokeDashoffset}
                                    strokeLinecap="round"
                                    className={`transition-all duration-300 ease-out ${stageInfo.color === 'blue' ? 'text-blue-500' :
                                        stageInfo.color === 'indigo' ? 'text-indigo-500' :
                                            stageInfo.color === 'purple' ? 'text-purple-500' :
                                                stageInfo.color === 'green' ? 'text-green-500' :
                                                    stageInfo.color === 'red' ? 'text-red-500' :
                                                        'text-gray-500'
                                        }`}
                                />
                            </svg>

                            {/* Percentage text */}
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="text-center">
                                    <div className={`text-2xl font-bold ${stageInfo.color === 'blue' ? 'text-blue-600' :
                                        stageInfo.color === 'indigo' ? 'text-indigo-600' :
                                            stageInfo.color === 'purple' ? 'text-purple-600' :
                                                stageInfo.color === 'green' ? 'text-green-600' :
                                                    stageInfo.color === 'red' ? 'text-red-600' :
                                                        'text-gray-600'
                                        }`}>
                                        {Math.round(progress)}%
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Stage Icon and Info */}
                        <div className="text-center">
                            <div className={`inline-flex p-3 rounded-full mb-2 ${stageInfo.color === 'blue' ? 'bg-blue-100' :
                                stageInfo.color === 'indigo' ? 'bg-indigo-100' :
                                    stageInfo.color === 'purple' ? 'bg-purple-100' :
                                        stageInfo.color === 'green' ? 'bg-green-100' :
                                            stageInfo.color === 'red' ? 'bg-red-100' :
                                                'bg-gray-100'
                                }`}>
                                <IconComponent className={`w-6 h-6 ${stageInfo.color === 'blue' ? 'text-blue-600' :
                                    stageInfo.color === 'indigo' ? 'text-indigo-600' :
                                        stageInfo.color === 'purple' ? 'text-purple-600' :
                                            stageInfo.color === 'green' ? 'text-green-600' :
                                                stageInfo.color === 'red' ? 'text-red-600' :
                                                    'text-gray-600'
                                    }`} />
                            </div>
                            <h4 className="text-sm font-medium text-gray-900 mb-1">{stageInfo.title}</h4>
                            <p className="text-xs text-gray-500">{stageInfo.description}</p>
                        </div>
                    </div>

                    {/* File Information */}
                    {fileName && (
                        <div className="bg-gray-50 rounded-lg p-4 mb-4">
                            <div className="flex items-center space-x-2 mb-2">
                                <DocumentArrowUpIcon className="w-4 h-4 text-gray-600 flex-shrink-0" />
                                <span className="text-sm font-medium text-gray-900 truncate">{fileName}</span>
                            </div>

                            <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                                {fileSize > 0 && (
                                    <div>
                                        <span className="font-medium">Size:</span> {formatFileSize(fileSize)}
                                    </div>
                                )}
                                {uploadSpeed > 0 && stage === 'uploading' && (
                                    <div>
                                        <span className="font-medium">Speed:</span> {formatSpeed(uploadSpeed)}
                                    </div>
                                )}
                                {elapsedTime > 0 && (
                                    <div>
                                        <span className="font-medium">Elapsed:</span> {formatTime(elapsedTime)}
                                    </div>
                                )}
                                {estimatedTimeRemaining > 0 && progress < 100 && (
                                    <div>
                                        <span className="font-medium">Remaining:</span> {formatTime(estimatedTimeRemaining)}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Data Processing Info */}
                    {(processedRecords > 0 || totalRecords > 0) && (
                        <div className="bg-blue-50 rounded-lg p-4 mb-4">
                            <h5 className="text-sm font-medium text-blue-900 mb-2">Data Processing</h5>
                            <div className="space-y-1 text-xs text-blue-800">
                                <div className="flex justify-between">
                                    <span>Processed:</span>
                                    <span className="font-medium">
                                        {processedRecords.toLocaleString()} / {totalRecords.toLocaleString()}
                                    </span>
                                </div>
                                {validRecords > 0 && (
                                    <div className="flex justify-between">
                                        <span>Valid records:</span>
                                        <span className="font-medium text-green-600">{validRecords.toLocaleString()}</span>
                                    </div>
                                )}
                                {invalidRecords > 0 && (
                                    <div className="flex justify-between">
                                        <span>Invalid records:</span>
                                        <span className="font-medium text-red-600">{invalidRecords.toLocaleString()}</span>
                                    </div>
                                )}
                                {duplicateRecords > 0 && (
                                    <div className="flex justify-between">
                                        <span>Duplicates:</span>
                                        <span className="font-medium text-yellow-600">{duplicateRecords.toLocaleString()}</span>
                                    </div>
                                )}
                                {contactsUpdated > 0 && (
                                    <div className="flex justify-between">
                                        <span>Contacts Updated:</span>
                                        <span className="font-medium text-purple-600">{contactsUpdated.toLocaleString()}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Database Write Progress */}
                    {(recordsWritten > 0 || recordsPending > 0 || stage === 'writing') && (
                        <div className="bg-purple-50 rounded-lg p-4 mb-4">
                            <h5 className="text-sm font-medium text-purple-900 mb-2">Database Write Progress</h5>
                            <div className="space-y-1 text-xs text-purple-800">
                                <div className="flex justify-between">
                                    <span>Records Written:</span>
                                    <span className="font-medium text-green-600">{recordsWritten.toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Records Pending:</span>
                                    <span className="font-medium text-orange-600">{recordsPending.toLocaleString()}</span>
                                </div>
                                {writeProgress > 0 && (
                                    <div className="mt-2">
                                        <div className="flex justify-between text-xs mb-1">
                                            <span>Write Progress:</span>
                                            <span className="font-medium">{Math.round(writeProgress)}%</span>
                                        </div>
                                        <div className="w-full bg-purple-200 rounded-full h-2">
                                            <div
                                                className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                                                style={{ width: `${writeProgress}%` }}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Progress Stages */}
                    <div className="space-y-2 mb-4">
                        {['uploading', 'parsing', 'validating', 'processing', 'writing'].map((stageName, index) => {
                            const isActive = stage === stageName;
                            const stageOrder = ['uploading', 'parsing', 'validating', 'processing', 'writing', 'completed'];
                            const currentStageIndex = stageOrder.indexOf(stage);
                            const isCompleted = currentStageIndex > index || stage === 'completed';

                            return (
                                <div key={stageName} className="flex items-center space-x-3">
                                    <div className={`w-2 h-2 rounded-full ${isCompleted ? 'bg-green-500' :
                                        isActive ? 'bg-blue-500 animate-pulse' :
                                            'bg-gray-300'
                                        }`} />
                                    <span className={`text-xs capitalize ${isCompleted ? 'text-green-600 font-medium' :
                                        isActive ? 'text-blue-600 font-medium' :
                                            'text-gray-500'
                                        }`}>
                                        {stageName.replace('_', ' ')}
                                    </span>
                                    {isCompleted && (
                                        <CheckCircleIcon className="w-3 h-3 text-green-500" />
                                    )}
                                    {isActive && (
                                        <ArrowPathIcon className="w-3 h-3 text-blue-500 animate-spin" />
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Error Message */}
                    {stage === 'error' && error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                            <div className="flex items-start space-x-2">
                                <ExclamationTriangleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                                <div>
                                    <h5 className="text-sm font-medium text-red-900 mb-1">Upload Failed</h5>
                                    <p className="text-xs text-red-700">{error}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Action Buttons */}
                    {stage === 'completed' && (
                        <button
                            onClick={onCancel}
                            className="w-full bg-green-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-green-700 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                        >
                            Continue
                        </button>
                    )}

                    {stage === 'error' && (
                        <div className="flex space-x-2">
                            <button
                                onClick={onCancel}
                                className="flex-1 bg-gray-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                            >
                                Close
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FileUploadProgress; 